<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Logo -->
      <b-link :to="{ path: '/' }" class="brand-logo">
        <b-img src="/images/CG-Logo-S.png" alt="Company Logo" />
      </b-link>

      <!-- Left Image -->
      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div class="w-100 d-flex justify-content-center">
          <b-img fluid :src="imgUrl" alt="Illustration" />
        </div>
      </b-col>

      <!-- Form -->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="mx-auto">

          <b-card-title tag="h2" class="font-weight-bold mb-1">
            Reset Password
          </b-card-title>

          <!-- ❌ Invalid token message -->
          <div v-if="!token" class="text-danger mb-2">
            Invalid or expired reset link.
          </div>

          <validation-observer ref="formRef" v-slot="{ invalid }">
            <b-form @submit.prevent="submitForm">

              <!-- New Password -->
              <b-form-group label="New Password">
                <validation-provider
                  name="New Password"
                  rules="required|min:6"
                  v-slot="{ errors }"
                >
                  <b-input-group :class="{ 'is-invalid': errors.length }">
                    <b-form-input
                      v-model.trim="form.password1"
                      :type="passwordFieldType"
                      placeholder="Enter new password"
                      :state="errors.length ? false : null"
                      :disabled="isLoading || !token"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                        class="cursor-pointer"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Confirm Password -->
              <b-form-group label="Confirm Password">
                <b-input-group :class="{ 'is-invalid': passwordMismatch }">
                  <b-form-input
                    v-model.trim="form.password2"
                    :type="passwordFieldType"
                    placeholder="Confirm password"
                    :disabled="isLoading || !token"
                  />
                </b-input-group>

                <small v-if="passwordMismatch" class="text-danger">
                  Password does not match
                </small>
              </b-form-group>

              <!-- Submit -->
              <b-button
                type="submit"
                variant="primary"
                block
                class="mt-3"
                :disabled="isLoading || passwordMismatch || invalid || !token"
              >
                <template v-if="isLoading">
                  <b-spinner small class="mr-1" /> Submitting...
                </template>
                <template v-else>
                  Submit
                </template>
              </b-button>

            </b-form>
          </validation-observer>

        </b-col>
      </b-col>

    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BSpinner,
} from "bootstrap-vue";

import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import api from "@/services/api";
import { handleApiError } from "@/utils/handleError";
import store from "@/store";

// Image loader
const getSideImage = (skin) =>
  skin === "dark"
    ? require("@/assets/images/pages/login-v2-dark.svg")
    : require("@/assets/images/pages/login-v2.svg");

export default {
  name: "PasswordReset",

  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },

  mixins: [togglePasswordVisibility],

  data() {
    return {
      form: {
        password1: "",
        password2: "",
      },
      isLoading: false,
      token: null,
      required,
    };
  },

  computed: {
    imgUrl() {
      return getSideImage(store.state.appConfig.layout.skin);
    },

    passwordToggleIcon() {
      return this.passwordFieldType === "password"
        ? "EyeIcon"
        : "EyeOffIcon";
    },

    passwordMismatch() {
      return (
        this.form.password1 &&
        this.form.password2 &&
        this.form.password1 !== this.form.password2
      );
    },
  },

  mounted() {
    // ✅ Get token from URL
    const uid = this.$route.query.uid;

    if (uid) {
      // decode in case it's encoded
      this.token = decodeURIComponent(uid);
    }
  },

  methods: {
    async submitForm() {
      if (this.isLoading || this.passwordMismatch || !this.token) return;

      const isValid = await this.$refs.formRef.validate();
      if (!isValid) return;

      this.isLoading = true;

      try {
        await api.post("/api/v1/forget-password-reset/", {
          token: this.token,
          password1: this.form.password1,
          password2: this.form.password2,
        });

        // ✅ Success
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            text: "Password has been changed successfully",
            icon: "CheckCircleIcon",
            variant: "success",
          },
        });

        // 🔥 Redirect
        this.$router.push("/");

      } catch (error) {
        const message = handleApiError(
          error,
          "Invalid or expired reset link"
        );

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: message,
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });

      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.brand-logo img {
  max-width: 150px;
}

.cursor-pointer {
  cursor: pointer;
}

.input-group-merge.is-invalid .form-control {
  border-color: #ea5455;
}
</style>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>